import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Anchor } from '@twilio-paste/anchor';
import { Button } from '@twilio-paste/button';
import { Box } from '@twilio-paste/box';
import { Card } from '@twilio-paste/card';
import { Paragraph } from '@twilio-paste/paragraph';
import { Stack } from '@twilio-paste/stack';
import { Text } from '@twilio-paste/text';
import { Alert } from '@twilio-paste/alert';
import { Toast, ToastContainer, Toaster, useToaster } from '@twilio-paste/toast';
import { useUID } from 'react-uid';
import { Input } from '@twilio-paste/input';
import { Label } from '@twilio-paste/label';
import { RadioGroup, Radio } from '@twilio-paste/radio-group';
import { DoDont, Do, Dont } from '../../../components/DoDont';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { Codeblock } from '../../../components/codeblock';
import { SidebarCategoryRoutes } from '../../../constants';
import { InlineCode } from '../../../components/Typography';
import { ToasterExample } from '../../../component-examples/ToastExamples';
import Changelog from '@twilio-paste/toast/CHANGELOG.md';
export const pageQuery = graphql`
  {
    allPasteComponent(filter: {name: {eq: "@twilio-paste/toast"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/components/toast/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name={props.pageContext.frontmatter.title} categoryRoute={SidebarCategoryRoutes.COMPONENTS} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/components/toast" storybookUrl="/?path=/story/components-toast--neutral" abstractUrl="https://share.goabstract.com/00bb0645-23b2-47aa-b57e-b0c6b1da9e8f" data={props.data.allPasteComponent.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Guidelines`}</h2>
        <h2>{`About Toast`}</h2>
        <p>{`Use a toast to communicate a direct response to a user based on their action. Using a toast lets users know that their action was acknowledged.`}</p>
        <p>{`Toasts appear over content on a page and stack vertically. All toasts have a close button so that users can dismiss any toasts that may be blocking underlying page content.`}</p>
        <p>{`Use a toast for one of these types of responses:`}</p>
        <ul>
          <li parentName="ul"><strong parentName="li">{`Success.`}</strong>{` Confirm a successful or completed action.`}</li>
          <li parentName="ul"><strong parentName="li">{`Warning.`}</strong>{` Help a user avoid a future issue that might happen based on their action. If the user can take an immediate action to avoid the issue, provide a way to do that. In most cases, a warning toast lets users know that their current action was successful but may have triggered a related issue, or that their action can't be completed because of an issue that they need to fix outside their current flow.`}</li>
          <li parentName="ul"><strong parentName="li">{`Error.`}</strong>{` Communicate an issue that's preventing a user from completing their current action and can be fixed within their current flow.`}</li>
          <li parentName="ul"><strong parentName="li">{`Neutral or general response.`}</strong>{` Let a user know that an asynchronous process has been triggered based on their action, especially if they have to wait for the process to finish before continuing a workflow. This could include a large download or a request that's been sent to a third party.`}</li>
        </ul>
        <p>{`In general, use Toasts for response to user action. To decide whether you should use Toasts or another type of messaging, jump to `}<a parentName="p" {...{
            "href": "#toast-vs-alerts-vs-inline-messaging"
          }}>{`Toasts vs. Alerts vs. Inline messaging`}</a>{`.`}</p>
        <h3>{`Accessibility`}</h3>
        <p>{`If you make a `}<a parentName="p" {...{
            "href": "#showing-dismissing-and-timing-out-a-toast"
          }}>{`toast automatically time out`}</a>{`, consider users that have different reading speeds, vision levels, and literacy levels (more details in the research insight below).`}</p>
        <p>{`We recommend giving users about 500 milliseconds per word. For short toasts (about 5–10 words), give users about 2,000–4,000 milliseconds to read the toast message. For toasts at our `}<a parentName="p" {...{
            "href": "#composition-notes"
          }}>{`max suggested length of 140 characters`}</a>{`, that's about 30,000 milliseconds for about 55 English words. In this case, consider not making the toast automatically time out since the longer content length could suggest that there's information worth persisting for the customer.`}</p>
        <p>{`In addition to reading time, consider the time users need to make a decision based on the message. If a toast has any interactive element in it, like a link or button, don't add an automatic time-out to the toast.`}</p>
        <Callout mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">Research insight into reading speeds</CalloutTitle>
  <CalloutText mdxType="CalloutText">
    From the{' '}
    <Anchor href="https://www.perkinselearning.org/technology/blog/reading-rates" mdxType="Anchor">Perkins School for the Blind</Anchor>:
    Reading rates are 1.5–2 times slower for people with low vision compared to those with sighted vision. If we take an
    8th grade reading level at 197 words per minute (wpm), we can suggest a 131 wpm reading rate.
  </CalloutText>
  <CalloutText mdxType="CalloutText">
    Additional research by{' '}
    <Anchor href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5557829/" mdxType="Anchor">
      researchers at the University of Alabama at Birmingham
    </Anchor>{' '}
    and by{' '}
    <Anchor href="https://www.microsoft.com/en-us/research/uploads/prod/2019/01/readerview_chi2019.pdf" mdxType="Anchor">
      Microsoft Research
    </Anchor>{' '}
    suggests that reading rates for adults with dyslexia or increased age are similar or slightly higher.
  </CalloutText>
        </Callout>
        <p>{`Don't use toasts to communicate dynamically changing content, including progress bars. Since toasts have ARIA `}<inlineCode parentName="p">{`role=status`}</inlineCode>{`, they'll need to trigger a status update to assistive technology, such as screen readers, each time the content changes, which could get annoying. Use a `}<a parentName="p" {...{
            "href": "/primitives/non-modal-dialog-primitive"
          }}>{`non-modal dialog`}</a>{`, `}<a parentName="p" {...{
            "href": "/components/modal"
          }}>{`modal`}</a>{`, or show the changing content on the underlying page instead.`}</p>
        <h2>{`Examples`}</h2>
        <p>{`Here are ways that you can compose neutral, warning, error, success, and dismissible toasts.`}</p>
        <Box marginBottom="space80" mdxType="Box">
  <Stack orientation="vertical" spacing="space40" mdxType="Stack">
    <Toast onDismiss={() => alert('dismissed')} variant="neutral" mdxType="Toast">
      <Text as="span" mdxType="Text">
        <strong>Martin Luther King, Jr.:</strong> We may have all come on different ships, but we’re in the same boat
        now.
      </Text>
    </Toast>
    <Toast onDismiss={() => alert('dismissed')} variant="warning" mdxType="Toast">
      <Text as="span" mdxType="Text">
        <strong>James Baldwin:</strong> Love takes off masks that we fear we cannot live without and know we cannot live
        within.
      </Text>
    </Toast>
    <Toast onDismiss={() => alert('dismissed')} variant="error" mdxType="Toast">
      <Text as="span" mdxType="Text">
        <strong>Chimamanda Ngozi Adichie:</strong> Racism should never have happened and so you don't get a cookie for
        reducing it.
      </Text>
    </Toast>
    <Toast onDismiss={() => alert('dismissed')} variant="success" mdxType="Toast">
      <Text as="span" mdxType="Text">
        <strong>Toni Morrison:</strong> You wanna fly, you got to give up the shit that weighs you down.
      </Text>
    </Toast>
  </Stack>
        </Box>
        <h3>{`Showing, dismissing, and timing out a toast`}</h3>
        <p>{`The Toaster is a convenient way to manage showing and dismissing Toasts. The Toaster comes in two parts:`}</p>
        <ul>
          <li parentName="ul">{`A `}<inlineCode parentName="li">{`useToaster`}</inlineCode>{` hook that handles adding, removing, auto dismissing and state.`}</li>
          <li parentName="ul">{`A `}<inlineCode parentName="li">{`<Toaster />`}</inlineCode>{` component that handles rendering toasts into the DOM.`}</li>
        </ul>
        <h4>{`useToaster hook`}</h4>
        <p>{`The easiest way to use the Toaster is to use the `}<inlineCode parentName="p">{`useToaster`}</inlineCode>{` hook. It will handle adding and removing a Toast and can be used to set automatic time-outs. The `}<inlineCode parentName="p">{`useToaster`}</inlineCode>{` hook will then return the props needed to render the Toasts within the `}<inlineCode parentName="p">{`Toaster`}</inlineCode>{` component.`}</p>
        <p><inlineCode parentName="p">{`useToaster`}</inlineCode>{` returns 3 things:`}</p>
        <ul>
          <li parentName="ul">{`An array of `}<inlineCode parentName="li">{`toasts`}</inlineCode>{` to be rendered in the DOM.`}</li>
          <li parentName="ul">{`A `}<inlineCode parentName="li">{`push`}</inlineCode>{` method used to add a Toast to the Toaster.`}</li>
          <li parentName="ul">{`A `}<inlineCode parentName="li">{`pop`}</inlineCode>{` method used to remove a Toast from the Toaster via its ID.`}</li>
        </ul>
        <p>{`Below is a form you can use to create toasts dynamically using the Toaster. The form data is used to `}<inlineCode parentName="p">{`push`}</inlineCode>{` a new Toast to the Toaster.`}</p>
        <Box marginBottom="space70" mdxType="Box">
  <Card mdxType="Card">
    <ToasterExample mdxType="ToasterExample" />
  </Card>
        </Box>
        <h5>{`Adding a Toast`}</h5>
        <p>{`You can push a Toast to the Toaster stack based on any action or event. In this example it's based on a button click:`}</p>
        <pre><code parentName="pre" {...{}}>{`const MyComponent = () => {
  const toaster = useToaster();

  const handleClick = () => {
    toaster.push({
      message: 'Selection was successfully saved to your profile',
      variant: 'success',
    })
  }

  return(
    <>
      <Button variant="primary" onClick={handleClick}>
        Add a toast
      </Button>
      <Toaster {...toaster} />
    </>
  )
}
`}</code></pre>
        <h5>{`Removing a Toast`}</h5>
        <p>{`You can optionally provide an `}<inlineCode parentName="p">{`id`}</inlineCode>{` to a toast when you push it to the stack. The ID can then be used to programmatically `}<inlineCode parentName="p">{`pop`}</inlineCode>{` the Toast from the stack:`}</p>
        <pre><code parentName="pre" {...{}}>{`toaster.push({
  message: 'Selection was successfully saved to your profile',
  variant: 'success',
  id: 'custom_id',
})

toaster.pop('custom_id')
`}</code></pre>
        <p>{`When providing your own custom ID, make sure they are unique.`}</p>
        <h5>{`Automatic dismissal`}</h5>
        <p>{`You can optionally provide a time-out to a toast, after which the Toast is automatically dismissed. This can be done by setting a `}<inlineCode parentName="p">{`dismissAfter`}</inlineCode>{` value. The value must be a valid time-out, set in milliseconds.`}</p>
        <pre><code parentName="pre" {...{}}>{`toaster.push({
  message: 'Selection was successfully saved to your profile',
  variant: 'success',
  id: 'custom_id',
  dismissAfter: 4000
})
`}</code></pre>
        <p>{`Add an automatic time-out if all these conditions are true:`}</p>
        <ul>
          <li parentName="ul">{`The user wouldn't need to remember or copy the information in order to continue with their current flow, or any future flow.`}</li>
          <li parentName="ul">{`The toast doesn't have any interactive content, such as a link or button.`}</li>
        </ul>
        <p>{`We recommend giving users about 500 milliseconds per word before timing out a toast.`}</p>
        <h4>{`Toaster component`}</h4>
        <p>{`The `}<inlineCode parentName="p">{`<Toaster />`}</inlineCode>{` component handles rendering the toasts it is passed via props. It handles how the Toast enters and leaves the UI.`}</p>
        <p>{`If you pass it the returned state of the `}<inlineCode parentName="p">{`useToaster`}</inlineCode>{` hook, state, pushing, popping, and handling browser window time-outs is taken care of for you.`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`const toaster = useToaster();

return <Toaster {...toaster} />;
`}</code></pre>
        <h3>{`Neutral Toast`}</h3>
        <p>{`Let a user know that a background process has been triggered based on their action, especially if they have to wait for the process to finish before continuing a flow.`}</p>
        <LivePreview scope={{
          Toast,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Toast onDismiss={() => alert('dismissed')} variant="neutral">
  <Text as="span">
    Your function is currently being deployed.
  </Text>
</Toast>`}
        </LivePreview>
        <h3>{`Success Toast`}</h3>
        <p>{`Use a success toast to confirm a successful or completed action. If a creation flow is a 2-step process—a user creates an object, then edits the details of that object—confirm the completion after each step with a toast.`}</p>
        <p>{`Provide an "undo" action in the toast if it's easily and safely reversible, and you're able to take the user back to their previous state. For example, you can allow the user to undo an action through a toast if:`}</p>
        <ul>
          <li parentName="ul">
            <p parentName="li">{`They submitted a short form that updates something. Undoing the action can take them back to their filled form with any sensitive fields cleared.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`They deleted non-sensitive information we'd want them to easily retrieve.`}</p>
          </li>
        </ul>
        <LivePreview scope={{
          Toast,
          Text,
          Anchor
        }} language="jsx" mdxType="LivePreview">
  {`<Toast onDismiss={() => alert('dismissed')} variant="success">
  <Text as="span">
    Your email address has been updated. You can view your profile <Anchor href="#">here</Anchor>.
  </Text>
</Toast>`}
        </LivePreview>
        <Callout mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">When to automatically time-out a success toast</CalloutTitle>
  <CalloutText mdxType="CalloutText">
    Add an automatic time-out to a success toast if the <i>only</i> thing it does is confirm that the action was
    completed. If it has any additional information or interactive content, like a link or button, the toast shouldn't
    time-out.
  </CalloutText>
  <CalloutText mdxType="CalloutText">
    Anecdotal data from product designers have also suggested that some Twilio customers find it annoying to have to
    manually close success toasts.
  </CalloutText>
        </Callout>
        <h3>{`Warning Toast`}</h3>
        <p>{`Use a warning toast to help a user avoid a potential future issue that might happen based on their action. If the user can take an immediate action to avoid the issue, provide a way to do that. In most cases, a warning toast lets users know that their current action completed but may have triggered a related issue, or that their action can't be completed because of an issue that they need to fix outside their current flow.`}</p>
        <p>{`Be cautious when using a warning toast because they can be stressful for a user to see. If the information you're trying to convey is not very critical, consider using a neutral toast instead.`}</p>
        <LivePreview scope={{
          Toast,
          Text,
          Anchor
        }} language="jsx" mdxType="LivePreview">
  {`<Toast onDismiss={() => alert('dismissed')} variant="warning">
  <Text as="span">
    The phone number has been deleted. Functions still using this phone number may fail. Verify there are no functions tied to that number <Anchor href="#">here</Anchor>.
  </Text>
</Toast>`}
        </LivePreview>
        <h3>{`Error Toast`}</h3>
        <p>{`Use an error toast to help a user understand an issue that's preventing them from completing their current action. Provide a way to resolve the issue either in the underlying page content or in the toast.`}</p>
        <p>{`Be cautious when using an error alert because they can be stressful for a user to see.`}</p>
        <LivePreview scope={{
          Toast,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Toast onDismiss={() => alert('dismissed')} variant="error">
  <Text as="span">
    There was an error when deleting the profile. Please try again.
  </Text>
</Toast>`}
        </LivePreview>
        <h2>{`Composition Notes`}</h2>
        <p>{`Keep toast text brief and scannable by placing only the highest priority information in it. Too much text in a toast can overwhelm a user. Aim for no more than 140 characters in a toast, and keep the text directly related to the user's current action. Success toasts generally run about 50 characters long, while all other alerts might run about 100 characters long. However, don't `}<a parentName="p" {...{
            "href": "/content/product-style-guide#be-direct-and-straightforward"
          }}>{`sacrifice being direct and straightforward`}</a>{` for a shorter message.`}</p>
        <p>{`Be specific in your response to the user's action and if applicable, provide a resolution by navigating a user to another page using an `}<a parentName="p" {...{
            "href": "/components/anchor/"
          }}>{`anchor`}</a>{`. Be cautious about using a button in a toast since it should be used only to trigger an action. If you must use a button, use a `}<a parentName="p" {...{
            "href": "/components/button#small-button"
          }}>{`small secondary button`}</a>{` in a toast.`}</p>
        <h2>{`When to use a Toast`}</h2>
        <p>{`Help users avoid scenarios where more than 3 toasts could show at a time. If more than 3 could appear at a time, show only one toast that summarizes the information you were intending to show in all of the toasts.`}</p>
        <h3>{`Toast vs. Alerts vs. Inline messaging`}</h3>
        <p>{`To communicate feedback on a user action, use a toast or in-page validation (`}<em parentName="p">{`e.g.`}</em>{`, full-page confirmation, inline error). You may also want to pair a toast with in-page validation. For example, when a user submits a long form, you can show an error toast that lets users know they need to fix some form fields, while also showing inline errors on each incorrect field.`}</p>
        <p>{`To communicate information from the system that isn't triggered by a user action, use an `}<a parentName="p" {...{
            "href": "/components/alert"
          }}>{`alert`}</a>{` or other in-page messaging.`}</p>
        <h5>{`Use Toasts when...`}</h5>
        <ul>
          <li parentName="ul">{`You need to communicate a direct response to a user action. Example: `}<em parentName="li">{`"asset.jpg" successfully added.`}</em></li>
          <li parentName="ul">{`You're providing only one link or button to help a user continue a flow.`}</li>
        </ul>
        <h5>{`Use Alerts when...`}</h5>
        <ul>
          <li parentName="ul">{`You need to notify a user about a high-priority or time-sensitive change that isn't triggered by a user action.`}</li>
          <li parentName="ul">{`You're providing only one link or button to help a user resolve an issue.`}</li>
        </ul>
        <h5>{`Use in-page messaging, e.g., inline errors, in-page call-outs when...`}</h5>
        <ul>
          <li parentName="ul">{`You need to communicate a direct and contextual response to a user action.`}</li>
          <li parentName="ul">{`You need to provide links or buttons next to an in-page element.`}</li>
          <li parentName="ul">{`You need to communicate dynamically changing content, like a progress bar. Use a `}<a parentName="li" {...{
              "href": "/primitives/non-modal-dialog-primitive"
            }}>{`non-modal dialog`}</a>{` instead.`}</li>
          <li parentName="ul">{`You need to communicate general promotional information to a user that is unrelated to completing their current action.`}</li>
        </ul>
        <h5>{`Use an alert dialog or modal when...`}</h5>
        <ul>
          <li parentName="ul">{`You need to interrupt a user flow.`}</li>
          <li parentName="ul">{`You need to provide multiple links or buttons to help a user continue a flow.`}</li>
        </ul>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Show only 1 toast in response to a single user action." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Avoid putting users in scenarios where more than 3 toasts could show at once. If you need to use more than 3, consider batching them." preview={false} mdxType="Dont" />
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use a toast to communicate a single, focused text status update." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Don't use toasts to communicate dynamically changing content, including progress bars. Use a non-modal dialog, modal, or show the changing content on the underlying page instead." preview={false} mdxType="Dont" />
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Allow toasts to persist on a page until a user closes them, especially if the toast has a link or button. Success toasts are an exception. Add an automatic time-out to a success toast if the only thing it does is confirm that the action was completed." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Don't add an automatic time-out to a toast if it has a link or button." preview={false} mdxType="Dont" />
        </DoDont>
        <hr></hr>
        <h2>{`Anatomy`}</h2>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Property`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default token`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Modifiable?`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`background-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-background-body`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-border-lighter`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-radius`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`border-radius-20`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-width`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`border-width-10`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-left-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><ul><li>{`Error: color-border-error-light`}</li><li>{`Neutral: color-border-neutral`}</li><li>{`Success: color-border-success-light`}</li><li>{`Warning: color-border-warning-light`}</li></ul></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-left-width`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`border-width-30`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`icon color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><ul><li>{`Error: color-text-error`}</li><li>{`Neutral: color-text-neutral`}</li><li>{`Success: color-text-success`}</li><li>{`Warning: color-text-warning`}</li><li>{`Close: color-text-icon`}</li></ul></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`icon size`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><ul><li>{`Alert icon: size-icon-20`}</li><li>{`Close icon: size-icon-20`}</li></ul></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`spacing`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><ul><li>{`Top padding: space-60`}</li><li>{`Bottom padding: space-60`}</li><li>{`left/right padding: space-60`}</li><li>{`Between icon and body: space-60`}</li><li>{`Between body and close button: space-40`}</li></ul></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`width`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`size-40`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
          </tbody>
        </table>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/toast - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {Toast, Toaster} from '@twilio-paste/toast';

const Component = () => {
  const toaster = useToaster();
  return <Toaster {...toaster} />;
};
`}</code></pre>
        <h5>{`Toaster component`}</h5>
        <p>{`If you choose to handle state entirely yourself, you can use `}<inlineCode parentName="p">{`<Toaster />`}</inlineCode>{` on its own, but you are fully responsible for handling state, time-outs, and the dismiss functionality.`}</p>
        <pre><code parentName="pre" {...{}}>{`<Toaster
  toasts={[{
    id: '',
    message: '',
    variant: ''
    dismissAfter: ''
  }]}
  pop={(id) =>{
    // called when a toast is dismissed
  }}
/>
`}</code></pre>
        <h4>{`Toast Props`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`children`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onDismiss?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`() => void`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Create dismissable toast by providing an onDismiss callback`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`variant`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`error`}</inlineCode>{` / `}<inlineCode parentName="td">{`neutral`}</inlineCode>{` / `}<inlineCode parentName="td">{`sucess`}</inlineCode>{` / `}<inlineCode parentName="td">{`warning`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <h4>{`useToaster return state`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`toasts`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ToasterToast[]`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Array of Toasts to render inside the toaster. See below`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`push?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(ToasterPush) => void`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Method used to push a Toast to the Toast stack`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`pop?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(id) => void`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Method used to pop a Toast from the Toast stack`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <h5>{`UseToasterToast`}</h5>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`id`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Unique identifier of the toast`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`message`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Message displayed in the toast`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`dismissAfter?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`number`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Timeout in milliseconds`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`setFocus?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets focus inside the Toast on render`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`true`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`variant`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`error`}</inlineCode>{` / `}<inlineCode parentName="td">{`neutral`}</inlineCode>{` / `}<inlineCode parentName="td">{`sucess`}</inlineCode>{` / `}<inlineCode parentName="td">{`warning`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <h4>{`Toaster Props`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`toasts`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ToasterToast[]`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Array of Toasts to render inside the toaster. See below`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`pop?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(id) => void`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Method called when a toast is dismissed`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`_`}{`console_patch?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`A temporary z-index fix for the Console product`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <h5>{`ToasterToast`}</h5>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`id`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Unique identifier of the toast`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`message`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Message displayed in the toast`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`setFocus?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets focus inside the Toast on render`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`variant`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`error`}</inlineCode>{` / `}<inlineCode parentName="td">{`neutral`}</inlineCode>{` / `}<inlineCode parentName="td">{`sucess`}</inlineCode>{` / `}<inlineCode parentName="td">{`warning`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <h4>{`ToastContainer Props`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`children`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`_`}{`console_patch?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`A temporary z-index fix for the Console product`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      